/**
 * VPCLIP / ВЭКРЕЗ command-line parser — the VPCLIP command surface (CLI-dispatch
 * half of VPCLIP-COMMAND, EPIC-VIEWPORT-CLIP L3).
 *
 * AutoCAD's VPCLIP clips a paper-space viewport to a boundary. The interactive
 * "Select viewport → Select clipping object / [Polygonal/Delete]" flow is the
 * separate VPCLIP-INTERACTIVE slice; THIS parser owns the SCRIPTED command-line
 * form (used by the command line, BATCH scripts and — once CC-2c wires the
 * translator — AI), which names the target viewport + boundary explicitly so it
 * is deterministic and testable:
 *
 *   VPCLIP <vpId> D                         — delete the clip (revert to rect)
 *   VPCLIP <vpId> P <x,y> <x,y> <x,y> [...]  — polygonal clip from ≥3 PAPER points
 *   VPCLIP <vpId> O <objectId>              — clip from a closed boundary OBJECT
 *   VPCLIP <vpId> <x,y> <x,y>               — rectangular clip from 2 corner points
 *   VPCLIP <vpId> <x,y> <x,y> <x,y> [...]    — polygonal clip (bare ≥3 points)
 *
 * The boundary points are PAPER-space (sheet) coordinates — the same frame the
 * viewport rectangle and a native VPCLIP boundary entity live in. This parser is
 * PURE (contracts-only, no back-edge to state/) so it lives in `dynamicInput/`
 * alongside the sibling `colorCommand` parser and is imported by BOTH the
 * interactive `submitCommandText` (commandDispatch) AND the store resolve seam
 * `resolveGeometryDocumentCommand` (state/storeHelpers) — one truth, so a
 * `commandline/submit` VPCLIP from `executeBatchCommands` / AI text resolves
 * identically to the interactive command line (@codex #496 P2). It returns a
 * typed `EditorCommand` or `null` when the text is NOT a VPCLIP command (so the
 * caller falls through to the generic command pipeline). Object → polygon
 * resolution is owned by the command-engine producer
 * (`applyViewportClipSetPolygon`); the parsed command just carries
 * `boundaryEntityId` (frozen to an explicit `boundary` at dispatch time for redo
 * fidelity — see the store's `recordedForwardOverride`).
 *
 * ezdxf reference: ezdxf `Viewport.clipping_boundary_handle`; Autodesk "VPCLIP
 * command" (Polygonal / Delete / select-object options).
 */

import type { EditorCommand } from "@dxf-ai-editor/contracts";

/** EN + RU aliases for the VPCLIP command (must mirror the command-registry descriptor). */
const VPCLIP_ALIASES = new Set(["VPCLIP", "-VPCLIP", "ВЭКРЕЗ"]);

/** Keyword sets for the three explicit options (EN + AutoCAD-CIS RU + short letters). */
const DELETE_KEYWORDS = new Set(["D", "DELETE", "У", "УД", "УДАЛИТЬ"]);
const OBJECT_KEYWORDS = new Set(["O", "О", "OBJECT", "ОБ", "ОБЪЕКТ"]);
const POLYGON_KEYWORDS = new Set([
  "P",
  "POLY",
  "POLYGONAL",
  "М",
  "МН",
  "МНОГОУГОЛЬНЫЙ",
]);

/** Parse an `x,y` coordinate token → `{x,y}`, or null when it is not a point. */
function parsePointToken(token: string): { x: number; y: number } | null {
  const parts = token.split(",");
  if (parts.length !== 2) return null;
  // @codex #496 P2 — reject a token with a MISSING coordinate component. A
  // trailing / leading comma (`"100,"`, `",80"`, or a bare `","`) splits into an
  // EMPTY part, and `Number("")` is `0` (not `NaN`) — so without this guard
  // `100,` would be silently accepted as `(100, 0)`, letting a typo in a command
  // script author the WRONG clip boundary. Require BOTH parts to carry a real
  // value (trim-tolerant, since the pure parser may be fed raw tokens) before
  // converting to numbers.
  const rawX = parts[0]!.trim();
  const rawY = parts[1]!.trim();
  if (rawX === "" || rawY === "") return null;
  const x = Number(rawX);
  const y = Number(rawY);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

/**
 * Parse every remaining token as an `x,y` point. Returns null when the list is
 * empty or ANY token is not a valid point (a malformed clip is rejected wholesale
 * rather than silently dropping bad vertices).
 */
function parsePointList(
  tokens: string[],
): Array<{ x: number; y: number }> | null {
  if (tokens.length === 0) return null;
  const points: Array<{ x: number; y: number }> = [];
  for (const token of tokens) {
    const point = parsePointToken(token);
    if (!point) return null;
    points.push(point);
  }
  return points;
}

/**
 * Parse VPCLIP command-line text into a typed viewport-clip `EditorCommand`.
 * Returns null when `text` is not a VPCLIP command OR is malformed (so the caller
 * falls through / surfaces the generic "not wired" path).
 */
export function parseVpclipCommandText(
  text: string,
  source: EditorCommand["source"],
): EditorCommand | null {
  const tokens = text.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return null;

  const verb = tokens[0]!.toUpperCase();
  if (!VPCLIP_ALIASES.has(verb)) return null;

  // <vpId> is mandatory; a bare `VPCLIP` with no target is not a scripted form
  // (the interactive pick is VPCLIP-INTERACTIVE) — return null so it is not
  // mis-dispatched as a successful no-arg clip.
  const viewportId = tokens[1];
  if (!viewportId) return null;

  const rest = tokens.slice(2);
  const first = rest[0]?.toUpperCase();

  // ── Delete: VPCLIP <vp> D ──────────────────────────────────────────────────
  // @codex #496 P2 — the delete form is DESTRUCTIVE (reverts the viewport to an
  // un-clipped rectangle) and takes NO further arguments. Require EXACTLY the
  // delete keyword after <vpId>: a trailing token (`VPCLIP 2A D 10,10 20,20`)
  // is a malformed line, not a delete. Reject it wholesale (return null) the
  // same way a bad point list is rejected below, rather than silently
  // discarding the extra tokens and still firing the removal.
  if (first !== undefined && DELETE_KEYWORDS.has(first)) {
    if (rest.length !== 1) return null;
    return {
      type: "geometry/viewport-clip-remove",
      source,
      viewportId,
    } as EditorCommand;
  }

  // ── From object: VPCLIP <vp> O <objectId> ──────────────────────────────────
  // Exactly the keyword + ONE object id — a trailing token past the object id
  // (`VPCLIP 2A O 3F 10,10`) is a malformed line, rejected wholesale rather
  // than silently ignored (@codex #496 P2, same trailing-token class as the
  // delete guard above).
  if (first !== undefined && OBJECT_KEYWORDS.has(first)) {
    const objectId = rest[1];
    if (!objectId || rest.length !== 2) return null;
    return {
      type: "geometry/viewport-clip-set-polygon",
      source,
      viewportId,
      boundaryEntityId: objectId,
    } as EditorCommand;
  }

  // ── Explicit polygonal: VPCLIP <vp> P <pt> <pt> <pt> [...] ──────────────────
  if (first !== undefined && POLYGON_KEYWORDS.has(first)) {
    const points = parsePointList(rest.slice(1));
    if (!points || points.length < 3) return null;
    return {
      type: "geometry/viewport-clip-set-polygon",
      source,
      viewportId,
      boundary: points,
    } as EditorCommand;
  }

  // ── Bare points: 2 → rectangular clip-set, ≥3 → polygonal ───────────────────
  const points = parsePointList(rest);
  if (!points) return null;
  if (points.length === 2) {
    return {
      type: "geometry/viewport-clip-set",
      source,
      viewportId,
      bounds: {
        x1: points[0]!.x,
        y1: points[0]!.y,
        x2: points[1]!.x,
        y2: points[1]!.y,
      },
    } as EditorCommand;
  }
  if (points.length >= 3) {
    return {
      type: "geometry/viewport-clip-set-polygon",
      source,
      viewportId,
      boundary: points,
    } as EditorCommand;
  }
  // A single bare point is not a valid clip boundary.
  return null;
}
